import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import { Button, Drawer, Select, toast } from '@saas-kit/ui';

import { forceCallToken } from './apiWorkstation';
import type { QmsCounter } from './types';

interface Props {
  open: boolean;
  tokenId: string;
  serviceId: string;
  displayCode: string;
  counters: QmsCounter[];
  onClose: () => void;
  onDone?: () => void;
}

/** Counter picker for the reception/supervisor force-call action. Only
 *  claimed counters that serve the token's service are offered; the
 *  backend still rejects a counter that is mid-service. */
export function ForceCallDialog({
  open,
  tokenId,
  serviceId,
  displayCode,
  counters,
  onClose,
  onDone,
}: Props): JSX.Element {
  const { t } = useTranslation('qms');
  const queryClient = useQueryClient();
  const [counterId, setCounterId] = useState('');

  useEffect(() => {
    if (open) setCounterId('');
  }, [open, tokenId]);

  const options = counters.filter(
    (counter) =>
      counter.is_active &&
      counter.is_claimed &&
      counter.service_ids.includes(serviceId),
  );

  const forceCall = useMutation({
    mutationFn: () => forceCallToken(tokenId, counterId),
    onSuccess: (updated) => {
      toast.success(
        t('issue.ops.force_called', { code: updated.display_code }),
      );
      void queryClient.invalidateQueries({ queryKey: ['qms-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['qms-issued-token'] });
      void queryClient.invalidateQueries({ queryKey: ['qms-counters'] });
      onDone?.();
      onClose();
    },
    onError: (error) => toast.error(parseApiError(error).message),
  });

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('issue.ops.send_title', { code: displayCode })}
      footer={
        <Button
          disabled={!counterId || forceCall.isPending}
          onClick={() => forceCall.mutate()}
        >
          {t('issue.ops.send_submit')}
        </Button>
      }
    >
      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('issue.ops.no_counters')}
        </p>
      ) : (
        <label className="block space-y-1 text-sm">
          <span className="text-foreground">{t('issue.ops.send_field')}</span>
          <Select
            value={counterId}
            onChange={(event) => setCounterId(event.target.value)}
          >
            <option value="">—</option>
            {options.map((counter) => (
              <option key={counter.id} value={counter.id}>
                {counter.claimed_by_name
                  ? `${counter.name} · ${counter.claimed_by_name}`
                  : counter.name}
              </option>
            ))}
          </Select>
        </label>
      )}
    </Drawer>
  );
}
